import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { type PropsWithChildren, useRef, useState } from 'react';

import { StatusButton } from '~/components/ui/status-button';

interface StripeElementsProviderProps {
  stripeClientSecret: string;
}

const stripePromise = loadStripe(window.ENV.STRIPE_PUBLISHABLE_KEY);

export function StripeElementsProvider({
  stripeClientSecret,
  children,
}: PropsWithChildren<StripeElementsProviderProps>) {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: stripeClientSecret,
      }}
    >
      {children}
    </Elements>
  );
}

interface StripePaymentFormProps {
  returnUrl: string;
  onPrePaymentCheck?: () => Promise<string | null>;
  stripePaymentButtonLabel?: string;
  disabled?: boolean;
  intentType?: 'payment' | 'setup';
}

export function StripePaymentForm({
  children,
  returnUrl,
  onPrePaymentCheck,
  stripePaymentButtonLabel,
  disabled,
  intentType,
}: React.PropsWithChildren<StripePaymentFormProps>) {
  const stripe = useStripe();
  const elements = useElements();
  const formRef = useRef<HTMLFormElement>(null);

  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    // Perform pre check if one has been provided, prevent payment if it fails
    const preCheckData = await onPrePaymentCheck?.();
    if (preCheckData !== undefined && preCheckData !== null) {
      setMessage(preCheckData);
      setIsLoading(false);

      // Scroll to top of form when precheck fails
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

      return;
    }

    const { error } =
      intentType === 'payment'
        ? await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: returnUrl,
            },
          })
        : await stripe.confirmSetup({
            elements,
            confirmParams: {
              return_url: returnUrl,
            },
          });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error.message ?? null);
    } else {
      setMessage('An unexpected error occurred.');
    }

    setIsLoading(false);
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit}>
      {message ? <div className="text-destructive">{message}</div> : null}

      {/* this small padding prevents a horizontal scrollbar */}
      <div className="p-1">
        <PaymentElement
          options={{
            layout: 'tabs',
            terms: {
              card: 'never',
            },
          }}
        />
      </div>

      {children}

      <div className="mt-6 flex justify-end">
        <StatusButton
          disabled={disabled || isLoading || !stripe || !elements}
          type="submit"
          status={isLoading ? 'pending' : 'idle'}
        >
          {stripePaymentButtonLabel ?? 'Pay Now'}
        </StatusButton>
      </div>
    </form>
  );
}
